import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';

interface FeatureFlagsState {
	flags: Record<string, boolean>;
	// Flags this environment refuses to change, keyed by flag name, with the
	// reason. Empty everywhere except demo and production [PTEC-710].
	locks: Record<string, string>;
	// When each ON flag was last switched, ISO. Feeds the "New" badge: a feature
	// that ships dark is new when it is switched on, not when it was released.
	enabledAt: Record<string, string>;
	loading: boolean;
	loaded: boolean;
	// Snapshot of each in-flight toggle's prior value, keyed by requestId, so a
	// rejected write can be rolled back to its exact previous state even when
	// multiple toggles for the same flag are in-flight and resolve out of order.
	pendingToggles: Record<string, boolean>;
}

const initialState: FeatureFlagsState = {
	flags: {},
	locks: {},
	enabledAt: {},
	loading: false,
	loaded: false,
	pendingToggles: {}
};

export const fetchFeatureFlags = createAsyncThunk<
	Record<string, boolean>,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('featureFlags/fetch', async () => {
	const response = await API.get('inspirusapi', '/feature-flags', {});
	return response;
});

export const toggleFeatureFlag = createAsyncThunk<
	{ name: string; enabled: boolean },
	{ name: string; enabled: boolean },
	{ dispatch: AppDispatch; state: RootState; rejectValue: { reason?: string } }
>('featureFlags/toggle', async ({ name, enabled }, { rejectWithValue }) => {
	try {
		await API.put('inspirusapi', `/feature-flags/${name}`, {
			headers: {},
			body: { enabled }
		});
		return { name, enabled };
	} catch (error) {
		// A thrown error is serialised down to name/message/stack, which loses the
		// response body — so a locked flag's 409 reason has to be carried out
		// deliberately or the caller has nothing to show [PTEC-712].
		const reason = (error as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
		return rejectWithValue({ reason });
	}
});

// Deliberately separate from fetchFeatureFlags, which runs on the guest login
// path from seven call sites. Only the admin screen needs the lock reasons, and
// if this request fails the screen simply renders the switch as usual — the
// write is still refused server-side and the failure snackbar explains it.
export const fetchFeatureFlagLocks = createAsyncThunk<
	Record<string, string>,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('featureFlags/fetchLocks', async () => {
	const response = await API.get('inspirusapi', '/feature-flags/locks', {});
	return response;
});

export const fetchFeatureFlagEnabledAt = createAsyncThunk<
	Record<string, string>,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('featureFlags/fetchEnabledAt', async () => {
	const response = await API.get('inspirusapi', '/feature-flags/enabled-at', {});
	return response as Record<string, string>;
});

const featureFlagsSlice = createSlice({
	name: 'featureFlags',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchFeatureFlags.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(fetchFeatureFlags.fulfilled, (state, action) => {
			state.flags = action.payload;
			state.loading = false;
			state.loaded = true;
		});
		builder.addCase(fetchFeatureFlags.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(fetchFeatureFlagLocks.fulfilled, (state, action) => {
			state.locks = action.payload;
		});
		builder.addCase(fetchFeatureFlagEnabledAt.fulfilled, (state, action) => {
			state.enabledAt = action.payload;
		});
		builder.addCase(fetchFeatureFlagEnabledAt.rejected, (state) => {
			// Timestamps only drive a badge. Losing them must never affect whether a
			// feature works, so this degrades to no badge rather than surfacing.
			state.enabledAt = {};
		});
		// Optimistically flip the flag the moment the toggle is dispatched so the
		// UI responds instantly, before the DynamoDB write completes. Capture the
		// flag's value before this write so we can restore it exactly on failure.
		builder.addCase(toggleFeatureFlag.pending, (state, action) => {
			const { name, enabled } = action.meta.arg;
			state.pendingToggles[action.meta.requestId] = state.flags[name];
			state.flags[name] = enabled;
		});
		builder.addCase(toggleFeatureFlag.fulfilled, (state, action) => {
			state.flags[action.payload.name] = action.payload.enabled;
			delete state.pendingToggles[action.meta.requestId];
		});
		// Roll back to the value captured before this specific write if it failed.
		builder.addCase(toggleFeatureFlag.rejected, (state, action) => {
			const { requestId } = action.meta;
			if (requestId in state.pendingToggles) {
				state.flags[action.meta.arg.name] = state.pendingToggles[requestId];
				delete state.pendingToggles[requestId];
			}
		});
	}
});

export const selectFeatureFlagsLoaded = (state: RootState) => state.featureFlags.loaded;
export const selectFeatureFlagsLoading = (state: RootState) => state.featureFlags.loading;

export default featureFlagsSlice.reducer;
